import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api, { safeExternalRedirect } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Countdown } from '../components/Countdown';
import { Crown, Check, Zap, Loader2, Sparkles, Infinity as InfinityIcon, Tag, X, Gift, Copy, Users, Trophy, Ticket } from 'lucide-react';

export default function UpgradePage() {
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // { code, label, applies_to }
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [ref, setRef] = useState(null); // my referral info
  const [featuredPromo, setFeaturedPromo] = useState(null); // active launch promo w/ expiry

  const load = async () => { const res = await api.get('/billing/summary'); setB(res.data); };
  const loadRef = async () => { try { const res = await api.get('/referral/me'); setRef(res.data); } catch (e) {} };
  useEffect(() => { load(); loadRef(); api.get('/public/promo').then((r) => setFeaturedPromo(r.data?.promo)).catch(() => {}); }, []);

  const applyCode = async (raw) => {
    const code = (raw ?? promoInput).trim();
    if (!code) return;
    setCheckingPromo(true);
    try {
      const res = await api.post('/payments/promo/validate', { code });
      if (res.data.valid) {
        setPromo({ code: res.data.code, label: res.data.label, applies_to: res.data.applies_to, type: res.data.type });
        setPromoInput(res.data.code);
        toast.success(res.data.message);
      } else {
        setPromo(null);
        toast.error(res.data.message || "That code isn't valid.");
      }
    } catch (e) {
      setPromo(null);
      toast.error("Couldn't check that code.");
    } finally { setCheckingPromo(false); }
  };

  // Auto-apply a referral code arriving via ?ref=CODE or a promo via ?code=CODE
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.get('ref');
    const c = q.get('code');
    if (r) { applyCode(r.toUpperCase()); window.history.replaceState({}, '', '/app/upgrade'); }
    else if (c) { applyCode(c.toUpperCase()); window.history.replaceState({}, '', '/app/upgrade'); }
    // eslint-disable-next-line
  }, []);

  const applyPromo = () => applyCode();
  const clearPromo = () => { setPromo(null); setPromoInput(''); };

  const [invite, setInvite] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const redeemInvite = async () => {
    const code = invite.trim();
    if (!code) return;
    setRedeeming(true);
    try {
      const res = await api.post('/access/redeem', { code });
      toast.success(`Invite redeemed — you now have Larkwell Pro (${res.data.label})! 🎉`);
      setInvite('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't redeem that code."); } finally { setRedeeming(false); }
  };

  const promoAppliesTo = (packageId) => promo && (promo.applies_to?.includes('all') || promo.applies_to?.includes(packageId));

  const shareUrl = ref ? `${window.location.origin}/app/upgrade?ref=${ref.code}` : '';
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Share link copied!'); }
    catch (e) { toast.error('Copy failed — select and copy manually.'); }
  };

  const checkout = async (package_id) => {
    setBusy(package_id);
    try {
      const payload = { package_id, origin_url: window.location.origin };
      if (promo && promoAppliesTo(package_id)) payload.promo_code = promo.code;
      const res = await api.post('/payments/checkout', payload);
      safeExternalRedirect(res.data.checkout_url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout'); setBusy(''); }
  };

  if (!b) return <div className="h-full grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>;
  const isPro = b.plan === 'pro';

  const proFeatures = ['Unlimited AI Plan My Day + task breakdowns', 'Unlimited agents & group chats', 'Calendar sync (Google & Outlook)', 'Texting, voice & call channels', 'Larger living memory'];

  return (
    <ScrollArea className="h-full">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2"><Crown className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /><h1 className="font-display text-2xl font-700">Upgrade Larkwell</h1></div>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">You have <b>{b.credits}</b> credits this month{isPro ? '' : ` (free plan refills ${b.free_monthly_credits}/mo)`}. Accessibility features are always free.</p>

        {/* Promo code */}
        <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-card p-4" data-testid="promo-code-box">
          <div className="flex items-center gap-2 mb-2"><Tag className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /><p className="text-sm font-600">Have a promo code?</p></div>
          {featuredPromo?.expires_at && (
            <div className="flex items-center gap-2 flex-wrap rounded-xl px-3 py-2 mb-3" style={{ background: 'hsl(var(--primary) / 0.08)' }} data-testid="promo-countdown-strip">
              <Zap className="h-3.5 w-3.5 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
              <span className="text-xs font-600"><span style={{ color: 'hsl(var(--primary))' }}>{featuredPromo.code}</span> · {featuredPromo.label} ends in</span>
              <span style={{ color: 'hsl(var(--primary))' }}><Countdown deadline={featuredPromo.expires_at} inline className="text-xs" testId="promo-countdown" onExpire={() => setFeaturedPromo(null)} /></span>
            </div>
          )}
          {promo ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="promo-applied">
              <Badge className="gap-1.5" style={{ background: 'hsl(var(--success))', color: 'white' }}>{promo.code} · {promo.label}</Badge>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">Applied at checkout for eligible plans.</span>
              <button onClick={clearPromo} className="ml-auto text-xs inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="promo-clear-button"><X className="h-3.5 w-3.5" /> Remove</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && applyPromo()} placeholder="e.g. LAUNCH25" className="h-10 rounded-xl uppercase" data-testid="promo-code-input" />
              <Button onClick={applyPromo} disabled={checkingPromo || !promoInput.trim()} variant="secondary" className="h-10 rounded-xl border border-[hsl(var(--border))] shrink-0" data-testid="promo-apply-button">{checkingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}</Button>
            </div>
          )}
        </div>

        {/* Invite / tester code */}
        {!isPro && (
          <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-card p-4" data-testid="invite-code-box">
            <div className="flex items-center gap-2 mb-2"><Ticket className="h-4 w-4" style={{ color: 'hsl(var(--accent))' }} /><p className="text-sm font-600">Have an invite code?</p></div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Redeem a tester/founding invite for free Larkwell Pro — full access, no payment.</p>
            <div className="flex items-center gap-2">
              <Input value={invite} onChange={(e) => setInvite(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && redeemInvite()} placeholder="e.g. LARKVIP-XXXXXX" className="h-10 rounded-xl uppercase" data-testid="invite-code-input" />
              <Button onClick={redeemInvite} disabled={redeeming || !invite.trim()} className="h-10 rounded-xl shrink-0" style={{ background: 'hsl(var(--accent))' }} data-testid="invite-redeem-button">{redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Redeem'}</Button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {/* Lifetime */}
          <div className="rounded-2xl border-2 p-6 bg-card relative overflow-hidden" style={{ borderColor: 'hsl(var(--primary))' }} data-testid="plan-lifetime">
            <Badge className="absolute top-4 right-4" style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>{b.seats_remaining} of {b.seats_total} left</Badge>
            <p className="text-sm font-600" style={{ color: 'hsl(var(--primary))' }}>Founding Member</p>
            <div className="flex items-end gap-1 mt-1"><span className="font-display text-4xl font-700">$99</span><span className="text-[hsl(var(--muted-foreground))] mb-1">once · lifetime</span></div>
            {promoAppliesTo('lifetime') && <p className="text-xs font-600 mt-1" style={{ color: 'hsl(var(--success))' }} data-testid="lifetime-promo-note">{promo.label} applied with {promo.code}</p>}
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Pro forever. Never pay again — limited to the first 100 members.</p>
            {b.founding_deadline && !b.founding_closed && (
              <div className="mt-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-3 flex items-center justify-between gap-2" data-testid="lifetime-countdown-box">
                <span className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide leading-tight">Offer<br />closes in</span>
                <Countdown deadline={b.founding_deadline} testId="upgrade-countdown" />
              </div>
            )}
            <ul className="mt-4 space-y-2">{proFeatures.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--success))' }} />{f}</li>)}</ul>
            {isPro ? (
              <Button disabled className="w-full rounded-xl mt-5" data-testid="lifetime-owned"><Crown className="h-4 w-4 mr-1.5" /> You're Pro</Button>
            ) : b.founding_expired ? (
              <Button disabled className="w-full rounded-xl mt-5" data-testid="lifetime-closed">Offer closed</Button>
            ) : b.seats_remaining <= 0 ? (
              <Button disabled className="w-full rounded-xl mt-5">Sold out</Button>
            ) : (
              <Button onClick={() => checkout('lifetime')} disabled={busy === 'lifetime'} className="w-full rounded-xl mt-5" style={{ background: 'hsl(var(--primary))' }} data-testid="buy-lifetime-button">{busy === 'lifetime' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Become a Founding Member'}</Button>
            )}
          </div>

          {/* Credits + subscription */}
          <div className="space-y-5">
            <div className="rounded-2xl border border-[hsl(var(--border))] p-6 bg-card" data-testid="plan-credits">
              <div className="flex items-center gap-2"><Zap className="h-4 w-4" style={{ color: 'hsl(var(--brand-honey))' }} /><p className="font-600">Credit top-up</p></div>
              <div className="flex items-end gap-1 mt-1"><span className="font-display text-3xl font-700">$9</span><span className="text-[hsl(var(--muted-foreground))] mb-1">for 500 credits</span></div>
              {promoAppliesTo('credits_500') && <p className="text-xs font-600 mt-1" style={{ color: 'hsl(var(--success))' }} data-testid="credits-promo-note">{promo.label} applied with {promo.code}</p>}
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">One-time boost for heavy planning weeks. Credits never expire.</p>
              <Button onClick={() => checkout('credits_500')} disabled={busy === 'credits_500'} variant="secondary" className="w-full rounded-xl mt-4 border border-[hsl(var(--border))]" data-testid="buy-credits-button">{busy === 'credits_500' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buy 500 credits'}</Button>
            </div>
            <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-6 bg-[hsl(var(--surface-2))]" data-testid="pro-subscription-card">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: 'hsl(var(--accent))' }} /><p className="font-600">Pro subscription</p><Badge variant="secondary" className="text-[10px]">coming soon</Badge></div>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-card border border-[hsl(var(--border))]">
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Monthly</span>
                  <span className="text-sm font-600">$9<span className="text-[hsl(var(--muted-foreground))] font-400">/mo</span></span>
                </div>
                <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-card border" style={{ borderColor: 'hsl(var(--primary) / 0.45)' }}>
                  <span className="text-sm font-600 flex items-center gap-2">Yearly <Badge className="text-[9px]" style={{ background: 'hsl(var(--success) / 0.15)', color: 'hsl(var(--success))' }} data-testid="annual-save-badge">Save 27%</Badge></span>
                  <span className="text-sm font-600 text-right">$79<span className="text-[hsl(var(--muted-foreground))] font-400">/yr</span><span className="block text-[10px] text-[hsl(var(--muted-foreground))] font-400">just $6.58/mo</span></span>
                </div>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Launching after the founding round. Grab lifetime Pro now to skip subscriptions forever.</p>
            </div>
          </div>
        </div>

        {/* Refer a friend */}
        {ref && (
          <div className="mt-6 rounded-2xl border p-6 bg-card" style={{ borderColor: 'hsl(var(--primary) / 0.4)' }} data-testid="referral-card">
            <div className="flex items-center gap-2"><Gift className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /><h2 className="font-display text-lg font-700">Invite friends, earn credits</h2></div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Share your code. Friends get <b>${ref.buyer_discount} off</b> their first purchase, and you earn <b>{ref.reward_credits} credits</b> each time one buys.</p>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 h-11 flex-1 min-w-0">
                <Tag className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
                <span className="font-mono font-700 tracking-wide truncate" data-testid="my-referral-code">{ref.code}</span>
                <span className="text-xs text-[hsl(var(--muted-foreground))] truncate ml-auto hidden sm:inline">{shareUrl}</span>
              </div>
              <Button onClick={copyShare} className="rounded-xl gap-1.5 h-11 shrink-0" style={{ background: 'hsl(var(--primary))' }} data-testid="copy-referral-button"><Copy className="h-4 w-4" /> Copy share link</Button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center" data-testid="referral-stat-count">
                <p className="font-display text-2xl font-700">{ref.referrals}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1"><Users className="h-3 w-3" /> friends joined</p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center" data-testid="referral-stat-credits">
                <p className="font-display text-2xl font-700" style={{ color: 'hsl(var(--primary))' }}>{ref.credits_earned}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">credits earned</p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--border))] p-3 text-center" data-testid="referral-stat-revenue">
                <p className="font-display text-2xl font-700">${ref.revenue_driven}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">revenue driven</p>
              </div>
            </div>

            {/* Milestone rewards */}
            {ref.milestones?.length > 0 && (
              <div className="mt-4 rounded-xl border border-[hsl(var(--border))] p-3" data-testid="referral-milestones">
                <div className="flex items-center gap-1.5 mb-2"><Trophy className="h-4 w-4" style={{ color: 'hsl(var(--brand-honey))' }} /><p className="text-sm font-600">Milestone bonuses</p></div>
                {ref.next_milestone ? (
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2" data-testid="referral-next-milestone">
                    <b>{ref.next_milestone.remaining}</b> more {ref.next_milestone.remaining === 1 ? 'friend' : 'friends'} to unlock a <b>{ref.next_milestone.bonus}-credit</b> bonus.
                  </p>
                ) : (
                  <p className="text-[11px] mb-2" style={{ color: 'hsl(var(--success))' }}>All milestone bonuses unlocked — you're a legend! 🎉</p>
                )}
                {ref.next_milestone && (
                  <Progress value={Math.min(100, (ref.referrals / ref.next_milestone.count) * 100)} className="h-2 mb-3" />
                )}
                <div className="flex flex-wrap gap-1.5">
                  {ref.milestones.map((m) => (
                    <Badge key={m.count} variant={m.reached ? 'default' : 'secondary'} className="text-[10px] gap-1" style={m.reached ? { background: 'hsl(var(--success))', color: 'white' } : {}} data-testid={`milestone-${m.count}`}>
                      {m.reached && <Check className="h-3 w-3" />}{m.count} friends → +{m.bonus} cr
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
